import React, { useState } from 'react';
import { API_BASE_URL } from '../config';

const FormBuilder = ({ eventId, formType, onFormCreated }) => {
  const [formTitle, setFormTitle] = useState('Call for Papers');
  const [formDescription, setFormDescription] = useState('Submit your talk proposal for our event');
  const [fields, setFields] = useState([
    {
      id: 1,
      field_type: 'text',
      field_label: 'Title of the Topic *',
      field_name: 'topic_title',
      placeholder: 'Enter the topic title',
      required: true,
      options: [],
      sort_order: 1
    }
  ]);

  const addField = () => {
    const newField = {
      id: Date.now(), // Use timestamp for unique ID
      field_type: 'text',
      field_label: 'New Field',
      field_name: `field_${fields.length + 1}`,
      placeholder: '',
      required: false,
      options: [],
      sort_order: fields.length + 1
    };
    setFields([...fields, newField]);
  };

  const removeField = (id) => {
    setFields(fields.filter(field => field.id !== id));
  };

  const updateField = (id, updates) => {
    setFields(fields.map(field => 
      field.id === id ? { ...field, ...updates } : field
    ));
  };

  const addOption = (fieldId) => {
    const field = fields.find(f => f.id === fieldId);
    const newOptions = [...(field.options || []), { value: '', label: '' }];
    updateField(fieldId, { options: newOptions });
  };

  const updateOption = (fieldId, optionIndex, updates) => {
    const field = fields.find(f => f.id === fieldId);
    const newOptions = field.options.map((option, idx) => 
      idx === optionIndex ? { ...option, ...updates } : option
    );
    updateField(fieldId, { options: newOptions });
  };

  const removeOption = (fieldId, optionIndex) => {
    const field = fields.find(f => f.id === fieldId);
    const newOptions = field.options.filter((_, idx) => idx !== optionIndex);
    updateField(fieldId, { options: newOptions });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_BASE_URL}/event/${eventId}/custom-forms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          form_type: formType,
          form_title: formTitle,
          form_description: formDescription,
          fields: fields
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert('Custom form created successfully! Speakers will now see this form when submitting proposals.');
        onFormCreated();
      } else {
        alert('Error creating form: ' + data.message);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error creating form. Please check your connection.');
    }
  };

  const fieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'email', label: 'Email' },
    { value: 'select', label: 'Dropdown' },
    { value: 'radio', label: 'Radio Buttons' },
    { value: 'checkbox', label: 'Checkbox' }
  ];

  return (
    <div className="form-builder">
      <h3>📋 Custom Form Builder</h3>
      
      <div className="form-builder-config">
        <div className="form-group">
          <label>Form Title *</label>
          <input
            type="text"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            placeholder="Enter form title"
            required
          />
        </div>
        
        <div className="form-group">
          <label>Form Description</label>
          <textarea
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="Enter form description"
            rows="3"
          />
        </div>
      </div>

      <div className="form-fields">
        <h4>🏗️ Form Fields</h4>
        <p className="field-instructions">Add and configure fields for your custom form:</p>
        
        {fields.map((field, index) => (
          <div key={field.id} className="field-builder">
            <div className="field-header">
              <h5>Field {index + 1}: {field.field_label}</h5>
              <button 
                type="button" 
                onClick={() => removeField(field.id)}
                className="btn btn-danger"
              >
                🗑️ Remove
              </button>
            </div>
            
            <div className="field-config">
              <div className="form-group">
                <label>Field Type *</label>
                <select
                  value={field.field_type}
                  onChange={(e) => updateField(field.id, { field_type: e.target.value })}
                >
                  {fieldTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Field Label *</label>
                <input
                  type="text"
                  value={field.field_label}
                  onChange={(e) => updateField(field.id, { field_label: e.target.value })}
                  placeholder="What users will see"
                  required
                />
              </div>

              <div className="form-group">
                <label>Field Name *</label>
                <input
                  type="text"
                  value={field.field_name}
                  onChange={(e) => updateField(field.id, { field_name: e.target.value.replace(/\s+/g, '_').toLowerCase() })}
                  placeholder="internal_field_name"
                  required
                />
                <small>No spaces, use underscores (e.g., speaker_name)</small>
              </div>

              <div className="form-group">
                <label>Placeholder Text</label>
                <input
                  type="text"
                  value={field.placeholder}
                  onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                  placeholder="Hint text inside the field"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) => updateField(field.id, { required: e.target.checked })}
                  />
                  Required Field
                </label>
              </div>

              {(field.field_type === 'select' || field.field_type === 'radio') && (
                <div className="field-options">
                  <label>Options *</label>
                  <p className="option-instructions">Add options for users to choose from:</p>
                  
                  {field.options.map((option, optionIndex) => (
                    <div key={optionIndex} className="option-row">
                      <input
                        type="text"
                        placeholder="Option value (internal)"
                        value={option.value}
                        onChange={(e) => updateOption(field.id, optionIndex, { value: e.target.value })}
                      />
                      <input
                        type="text"
                        placeholder="Display label"
                        value={option.label}
                        onChange={(e) => updateOption(field.id, optionIndex, { label: e.target.value })}
                      />
                      <button 
                        type="button" 
                        onClick={() => removeOption(field.id, optionIndex)}
                        className="btn btn-small"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  
                  <button 
                    type="button" 
                    onClick={() => addOption(field.id)}
                    className="btn btn-secondary"
                  >
                    ➕ Add Option
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        
        <button type="button" onClick={addField} className="btn btn-primary">
          ➕ Add New Field
        </button>
      </div>

      <div className="form-builder-actions">
        <button type="button" onClick={handleSubmit} className="btn btn-success">
          🚀 Create Custom Form
        </button>
        <button type="button" onClick={onFormCreated} className="btn btn-secondary">
          Cancel
        </button>
      </div>

      <div className="form-preview">
        <h4>👀 Form Preview</h4>
        <div className="preview-container">
          <h5>{formTitle}</h5>
          {formDescription && <p>{formDescription}</p>}
          
          {fields.map(field => (
            <div key={field.id} className="preview-field">
              <label>
                {field.field_label}
                {field.required && <span className="required"> *</span>}
              </label>
              
              {field.field_type === 'text' || field.field_type === 'email' ? (
                <input 
                  type={field.field_type} 
                  placeholder={field.placeholder} 
                  disabled 
                />
              ) : field.field_type === 'textarea' ? (
                <textarea 
                  placeholder={field.placeholder} 
                  rows="3" 
                  disabled 
                />
              ) : field.field_type === 'select' ? (
                <select disabled>
                  <option value="">{field.placeholder || 'Select an option'}</option>
                  {field.options.map((opt, idx) => (
                    <option key={idx} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : field.field_type === 'radio' ? (
                <div className="radio-preview">
                  {field.options.map((opt, idx) => (
                    <label key={idx} className="radio-option">
                      <input type="radio" name={`preview_${field.id}`} disabled />
                      {opt.label}
                    </label>
                  ))}
                </div>
              ) : field.field_type === 'checkbox' ? (
                <label className="checkbox-preview">
                  <input type="checkbox" disabled />
                  {field.field_label}
                </label>
              ) : null}
            </div>
          ))}
          
          <button type="button" disabled className="btn btn-primary">
            Submit Proposal
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
